import React from "react";
import "../../styles/process-advisory-modal.css";

const COLUMNS = ["Element", "Category", "Severity", "Anomaly", "Rule Violated", "Action", "Advisory"];

/**
 * ProcessAdvisoryModal
 * Reports the BPMN advisory findings returned by /bpmn-native/analyze/{id} and
 * offers to restructure the process (auto-fix the findings).
 *
 * All rows are read from the response's `advisory.findings[]` — see the analyze
 * endpoint contract. Presentational only: the page owns the API calls and state.
 *
 * Props:
 * - open: boolean
 * - processName: string — header title
 * - verdict: string — "invalid" renders the warning badge, anything else "valid"
 * - findings: advisory.findings[] from the analyze response
 * - restructuring: boolean — restructure call in flight
 * - restructureError: string — restructure failure message
 * - onRestructure(): run the restructure
 * - onCancel(): dismiss
 */
function ProcessAdvisoryModal({
  open,
  processName = "",
  verdict = "",
  findings = [],
  restructuring = false,
  restructureError = "",
  onRestructure,
  onCancel,
}) {
  if (!open) return null;

  const rows = Array.isArray(findings) ? findings : [];

  return (
    <div
      className="pa-modal-overlay"
      role="dialog"
      aria-modal="true"
      onClick={(e) => { if (e.target === e.currentTarget) onCancel?.(); }}
    >
      <div className="pa-modal">
        <div className="pa-modal-header">
          <div className="pa-modal-verdict">
            {verdict === "invalid" ? (
              <span className="pa-verdict-badge invalid">⚠ Invalid</span>
            ) : (
              <span className="pa-verdict-badge valid">✓ Valid</span>
            )}
          </div>
          <h2 className="pa-modal-process-name">{processName || "Process Analysis"}</h2>
          <button className="pa-modal-close" onClick={onCancel} aria-label="Close">✕</button>
        </div>

        <div className="pa-modal-body">
          {rows.length === 0 ? (
            <div className="pa-no-findings">No findings — the process appears structurally sound.</div>
          ) : (
            <div className="pa-table-wrap">
              <table className="pa-findings-table">
                <thead>
                  <tr>
                    {COLUMNS.map((h) => (
                      <th key={h}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((f, i) => (
                    <tr key={f.id || i} className={`pa-row-severity-${(f.severity || "minor").toLowerCase()}`}>
                      <td className="pa-cell-name">{f.element_name || "-"}</td>
                      <td>
                        <span className={`pa-cat-chip pa-cat-${(f.category || "").replace(/_/g, "-")}`}>
                          {f.category ? f.category.replace(/_/g, " ") : "-"}
                        </span>
                      </td>
                      <td>
                        <span className={`pa-sev-badge pa-sev-${(f.severity || "minor").toLowerCase()}`}>
                          {f.severity || "-"}
                        </span>
                      </td>
                      <td className="pa-cell-long">{f.anomaly || "-"}</td>
                      <td className="pa-cell-long">{f.rule_violated || "-"}</td>
                      <td>{f.action ? <span className="pa-action-chip">{f.action}</span> : "-"}</td>
                      <td className="pa-cell-long">{f.advisory_text || "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {restructureError && (
            <div className="pa-modal-error">
              <strong>⚠ Restructure failed:</strong> {restructureError}
            </div>
          )}
        </div>

        <div className="pa-modal-footer">
          <button type="button" className="secondary-btn" onClick={onCancel} disabled={restructuring}>
            Cancel
          </button>
          <button
            type="button"
            className="primary-btn pa-restructure-btn"
            onClick={onRestructure}
            disabled={restructuring}
          >
            {restructuring ? (
              <><span className="pa-btn-spinner" /> Restructuring…</>
            ) : (
              "Restructure Process"
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

export default ProcessAdvisoryModal;
